'use client'

import { motion } from 'framer-motion'
import {
  ArrowRight,
  Check,
  Database,
  MessageSquare,
  Users,
  Store,
  Bot,
  BarChart3,
  Shield,
  Zap,
  Target,
  Layers,
  Workflow,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Rocket,
  TrendingDown,
  CalendarCheck,
  Lock,
} from 'lucide-react'

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5, ease: 'easeOut' as const },
  }),
}

// ============================================================
// DADOS
// ============================================================

const dores = [
  {
    icon: Users,
    titulo: 'Revendedoras abandonadas',
    desc: 'Base grande, mas supervisores não conseguem dar atenção. Revendedoras reclamam de falta de contato. Uma objeção e o supervisor desiste.',
    evidencia: '99 conversas sem resposta (53,5% do inbox). 65% das perguntas poderiam ser automatizadas.',
  },
  {
    icon: Database,
    titulo: 'CRM fechado, dados presos',
    desc: 'O CRM da franqueadora não libera dados completos. Tudo que importa (ciclo, segmentação, flag) só aparece na tela. Sem API, sem exportação útil.',
    evidencia: 'API pública do ManyChat não expõe histórico. Dados extraídos via navegação direta.',
  },
  {
    icon: AlertTriangle,
    titulo: 'Dependência da Raíssa',
    desc: 'Hoje, só a Raíssa sabe operar a régua de relacionamento. Isso trava o crescimento e consome energia que deveria ir para gestão estratégica.',
    evidencia: '80 de 86 conversas atribuídas são dela (93%). Quando ela para, o atendimento para.',
  },
  {
    icon: Store,
    titulo: 'Lojas físicas sem controle',
    desc: '17 lojas, 34 WhatsApp, zero rastreabilidade. Não dá para saber quem atendeu o cliente, quem xingou, quem não respondeu. Os contatos dos clientes ficam presos no aparelho.',
    evidencia: '4 canais conectados, zero growth tools. Instagram e Messenger sem automação.',
  },
  {
    icon: TrendingDown,
    titulo: 'Base encolhendo',
    desc: 'A base perde mais revendedoras do que ganha. Sem campanha de reativação, sem captação automatizada, sem onboarding para entrantes.',
    evidencia: '1.190 cessados (14,5%) contra apenas 33 entrantes (0,4%). A base perde 36x mais do que ganha.',
  },
]

const plataformaCards = [
  {
    icon: MessageSquare,
    titulo: 'Inbox centralizado',
    desc: 'Todas as conversas de WhatsApp em um único painel. Atribuição por supervisor, filtros por status, busca por contato. Fim do "quem respondeu?".',
  },
  {
    icon: Bot,
    titulo: 'Agentes de IA (Claude)',
    desc: 'IA que responde automaticamente com personalidade da marca. Fases de conversa, regras de comportamento, transferência para humano por keyword.',
  },
  {
    icon: Workflow,
    titulo: 'Automação de funis',
    desc: 'Funis por estágio de ciclo que rodam sozinhos. A revendedora entra no funil certo baseado no seu status. Sem intervenção manual.',
  },
  {
    icon: BarChart3,
    titulo: 'Dashboard de gestão',
    desc: 'Painel em tempo real com métricas de disparo assertivo: quem recebeu, quem leu, quem clicou, quem comprou. Visão por central e por supervisor.',
  },
]

const projeto1Componentes = [
  {
    icon: Database,
    titulo: 'Banco de dados proprietário',
    desc: 'Modelo que cruza ciclo de inatividade (A0 a A17), papel de fidelidade (Bronze até Diamante) e equipe/central. Base multi-marca unificada: Eudora, QDB e Oui em um único banco - a diferenciação de marca acontece no criativo por campanha.',
  },
  {
    icon: Bot,
    titulo: 'Ingestão automática',
    desc: 'Bot que todo dia de manhã baixa a planilha do Drive, normaliza telefones para o padrão E.164 brasileiro, detecta mudanças de status e prepara o banco para os disparos do dia. Um ciclo completo tem aproximadamente 21 dias.',
  },
  {
    icon: Workflow,
    titulo: 'Funis por estágio',
    desc: 'No mínimo 3 funis iniciais, cada um com copy e criativo adequados ao ciclo de inatividade e ao papel de fidelidade. Quem compra não reseta o fluxo: segue o status do dia seguinte.',
  },
  {
    icon: MessageSquare,
    titulo: 'Disparo multi-chip',
    desc: 'Migração dos 18 chips atuais (1 por supervisor) para 3 chips (1 por central: Cruzeiro, Guará, Caçapava). Cada chip respeita limites da Meta e faz disparo segmentado.',
  },
  {
    icon: Zap,
    titulo: 'Broadcast paralelo',
    desc: 'Campanhas pontuais (aniversário, data comemorativa, oferta relâmpago) rodam em paralelo à régua sem interromper o fluxo de relacionamento contínuo.',
  },
  {
    icon: BarChart3,
    titulo: 'Dashboard de gestão',
    desc: 'Painel que mostra quantas revendedoras estão em cada estágio, quem recebeu, quem abriu, quem clicou, quem converteu em pedido. Base para o conceito de disparo assertivo.',
  },
]

const projeto2Componentes = [
  {
    icon: MessageSquare,
    titulo: 'Centralização dos WhatsApp',
    desc: 'Os 34 aparelhos das lojas (17 lojas x 2 chips) passam a ser espelhados em um painel central. Conversas visíveis em tempo real.',
  },
  {
    icon: Shield,
    titulo: 'Auditoria de atendimento',
    desc: 'Histórico completo de quem atendeu, quando, o quê. Essencial para tomada de decisão sobre desligamento de funcionários e proteção da Raíssa em casos de reclamação.',
  },
  {
    icon: Database,
    titulo: 'Captura de base de clientes',
    desc: 'Todo cliente que conversa com a loja entra em um banco centralizado. Chega de perder contatos quando troca o celular ou a vendedora sai.',
  },
  {
    icon: Target,
    titulo: 'Campanhas para clientes da loja',
    desc: 'Com a base capturada, começa a ser possível fazer campanhas genéricas (dia dos namorados, dia das mães) para quem já comprou em loja física.',
  },
]

const fases = [
  {
    numero: '0',
    titulo: 'Fundação',
    cor: 'from-slate-500 to-slate-700',
    duracao: '1 semana',
    itens: [
      'Documentar as regras completas (enum de atividade A0–A17, papel Bronze/Prata/Ouro/Platina/Rubi/Diamante, mapa de cores)',
      'Importar e validar de-para cod estrutura → central (Excel já recebido)',
      'Confirmar link drive com planilha atualizada diariamente pela analista Boticário',
      'Modelar banco de dados multi-marca no Supabase (Eudora, QDB, Oui)',
      'Criar BM nova e verificar empresa no Facebook',
    ],
  },
  {
    numero: '1',
    titulo: 'MVP em uma central',
    cor: 'from-teal-500 to-teal-700',
    duracao: '2 semanas',
    itens: [
      'Pipeline de ingestão da planilha rodando todo dia 6h',
      'Banco de dados alimentado e validado (ciclo de ~21 dias)',
      '1 funil completo (por exemplo, recuperação A3 Ouro/Platina - Eudora)',
      '1 central escolhida (a menor, para validar ponta a ponta)',
      'Disparos reais com criativos da agência - meta de 6.000 disparos/ciclo',
      'Dashboard básico de acompanhamento',
    ],
  },
  {
    numero: '2',
    titulo: 'Escala para 3 centrais',
    cor: 'from-amber-500 to-amber-600',
    duracao: '3 semanas',
    itens: [
      'Migração completa de 18 chips para 3 chips (1 por central)',
      'Funis para ciclos críticos (A0, A3, A6) para Eudora, QDB e Oui',
      'Volume de 6.000+ disparos por ciclo de ~21 dias consolidado nas 3 centrais',
      'Motor de broadcast paralelo (campanhas pontuais sem interromper régua)',
      'Regras avançadas: comportamento de clique ramifica o funil',
      'Relatório por ciclo de disparo assertivo',
    ],
  },
  {
    numero: '3',
    titulo: 'Gestão de lojas físicas',
    cor: 'from-rose-500 to-rose-700',
    duracao: '3 semanas',
    itens: [
      'Conexão dos 34 WhatsApp das lojas ao painel central',
      'Auditoria de conversas por loja, por vendedora',
      'Captura automática de contatos de clientes',
      'Log completo para histórico e decisões de RH',
    ],
  },
  {
    numero: '4',
    titulo: 'Evolução futura',
    cor: 'from-indigo-500 to-indigo-700',
    duracao: 'contínuo',
    itens: [
      'Substituição da planilha Drive por RPA direto no CRM (quando Boticário descontinuar)',
      'Campanhas para clientes das lojas físicas',
      'Integração com Instagram',
      'Régua transacional pós-compra (parabéns pela compra, sugestão de recompra)',
    ],
  },
]

const pontosConfirmacao = [
  { pergunta: 'Escala do campo Papel', resposta: 'Bronze, Prata, Ouro, Platina, Rubi, Diamante - 6 níveis confirmados' },
  { pergunta: 'Duração de um ciclo', resposta: 'Aproximadamente 21 dias por ciclo (A1 = ~21 dias sem compra)' },
  { pergunta: 'Composição da base', resposta: 'Multi-marca: Eudora, QDB (Quem Disse Berenice) e Oui juntos em uma base unificada' },
  { pergunta: 'Tabela de-para centrais', resposta: 'Já recebido em Excel - de-para cod estrutura → central pronto para importar' },
  { pergunta: 'Volume de disparos', resposta: 'Mínimo de 6.000 disparos por ciclo (~21 dias) nas 3 centrais' },
  { pergunta: 'Orçamento mensal', resposta: 'R$8.000/mês - cobre IA + disparos e substitui 2 funcionários da operação de relacionamento' },
]

const investimentoProjeto1 = [
  { item: 'Projeto, modelagem de banco e setup inicial', detalhe: 'Única vez, na fundação' },
  { item: 'Desenvolvimento do sistema (fases 1 e 2)', detalhe: 'Parcelado conforme entregas validadas' },
  { item: 'Setup de 3 chips + BM nova + verificação Meta', detalhe: 'Única vez' },
  { item: 'Mensalidade R$8.000/mês (IA + disparos)', detalhe: 'Infraestrutura, banco, API WhatsApp, IA e volume base de 6.000 disparos/ciclo' },
  { item: 'ROI previsto: substituição de 2 funcionários', detalhe: 'Budget confirmado cobre IA + disparos e libera headcount para realocar ou desligar' },
  { item: 'Criativos (copy e design)', detalhe: 'Por conta da agência dela (já validado na reunião)' },
]

const resultadosEsperados = [
  {
    metrica: '400+',
    descricao: 'revendedoras reativadas com régua automatizada no primeiro ciclo de 21 dias',
  },
  {
    metrica: '65%',
    descricao: 'das perguntas automatizadas pela IA - comprovado na auditoria de 20+ conversas reais',
  },
  {
    metrica: '-2',
    descricao: 'funcionários da operação de relacionamento substituídos pela automação dentro do budget de R$8k/mês',
  },
  {
    metrica: '99→0',
    descricao: 'conversas sem resposta eliminadas na primeira semana com IA + distribuição automática',
  },
]

// ============================================================
// PÁGINA
// ============================================================

export default function BoticarioPage() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-rose-950 px-6 py-20 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(236,72,153,0.15),transparent_50%)]" />
        <div className="relative mx-auto max-w-5xl">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0}>
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-2 text-sm backdrop-blur">
              <Rocket className="h-4 w-4 text-rose-300" />
              <span>Proposta técnica e comercial</span>
            </div>
            <h1 className="mb-6 text-4xl font-bold leading-tight md:text-6xl">
              Régua de Relacionamento e<br />
              Gestão de Lojas Boticário
            </h1>
            <p className="max-w-3xl text-xl text-slate-300">
              Um sistema sob medida para reativar sua base de revendedoras, auditar
              o atendimento das 17 lojas e transformar a operação em uma máquina
              que não depende mais só de você.
            </p>
            <div className="mt-8 flex flex-wrap gap-4 text-sm text-slate-300">
              <span className="rounded-full bg-white/10 px-4 py-2">Para: Raíssa Menezes Bustamante</span>
              <span className="rounded-full bg-white/10 px-4 py-2">Por: Jonathan Machado | eBuz</span>
              <span className="rounded-full bg-white/10 px-4 py-2">11 de abril de 2026</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* DIAGNOSTICO */}
      <section className="px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">O que ouvi na nossa conversa</h2>
            <p className="mb-12 max-w-3xl text-lg text-slate-600">
              Antes de propor solução, registrei o diagnóstico. Se algum ponto abaixo
              estiver errado, é sinal de que precisamos alinhar antes de seguir.
            </p>
          </motion.div>
          <div className="grid gap-6 md:grid-cols-2">
            {dores.map((dor, i) => (
              <motion.div
                key={dor.titulo}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="rounded-2xl border border-slate-200 bg-slate-50 p-6"
              >
                <dor.icon className="mb-4 h-10 w-10 text-rose-600" />
                <h3 className="mb-2 text-xl font-semibold">{dor.titulo}</h3>
                <p className="text-slate-600">{dor.desc}</p>
                {'evidencia' in dor && (
                  <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
                    Comprovado: {(dor as { evidencia: string }).evidencia}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROJETO 1 */}
      <section className="bg-slate-50 px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <div className="mb-2 inline-block rounded-full bg-rose-100 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-rose-700">
              Projeto 1
            </div>
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">Régua de Revendedoras (AVD)</h2>
            <p className="mb-12 max-w-3xl text-lg text-slate-600">
              Um sistema que escuta o CRM da franqueadora, entende em qual estágio
              cada revendedora está, e dispara a mensagem certa no dia certo. Tudo
              de forma automatizada, rastreada e escalável.
            </p>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {projeto1Componentes.map((comp, i) => (
              <motion.div
                key={comp.titulo}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <comp.icon className="mb-3 h-8 w-8 text-rose-600" />
                <h3 className="mb-2 text-lg font-semibold">{comp.titulo}</h3>
                <p className="text-sm text-slate-600">{comp.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* A PLATAFORMA */}
      <section className="px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <div className="mb-2 inline-block rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-emerald-700">
              Sistema proprio - nao depende de ManyChat
            </div>
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">A plataforma: Funil Chatbot</h2>
            <p className="mb-12 max-w-3xl text-lg text-slate-600">
              Plataforma de atendimento via WhatsApp com IA que ja esta funcionando.
              Inbox centralizado, agentes inteligentes e automacao de funis em um unico sistema.
            </p>
          </motion.div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {plataformaCards.map((card, i) => (
              <motion.div
                key={card.titulo}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <card.icon className="mb-3 h-8 w-8 text-emerald-600" />
                <h3 className="mb-2 text-lg font-semibold">{card.titulo}</h3>
                <p className="text-sm text-slate-600">{card.desc}</p>
              </motion.div>
            ))}
          </div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={4} className="mt-8 rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center">
            <p className="text-lg font-semibold text-emerald-800">Sistema em producao: app.funilchatbot.com.br</p>
            <p className="mt-1 text-sm text-emerald-700">Inbox, IA, automacao e canais WhatsApp ja funcionando. Pronto para conectar suas 3 centrais.</p>
          </motion.div>
        </div>
      </section>

      {/* ESTRUTURA DE DADOS */}
      <section className="px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">Estrutura de dados do projeto</h2>
            <p className="mb-8 text-lg text-slate-600">
              Analisei a planilha que você compartilhou. A base já tem o essencial
              para começarmos: identificação da revendedora, equipe/supervisor,
              telefone, ciclo de inatividade e papel de fidelidade. Abaixo, o que
              vamos modelar no banco de dados próprio.
            </p>
          </motion.div>

          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-slate-900 text-white">
                <tr>
                  <th className="p-4 text-left font-semibold">Coluna da planilha</th>
                  <th className="p-4 text-left font-semibold">Papel no sistema</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {[
                  ['Cod Estrutura', 'Chave da equipe/supervisor. Será vinculada à central que atende.'],
                  ['Nome Estrutura', 'Nome da equipe, usado para identificar o supervisor responsável.'],
                  ['Cod Revendedor', 'Identificador único da revendedora. Chave primária no banco.'],
                  ['Nome Revendedor', 'Usado na personalização da mensagem.'],
                  ['Tel. Celular', 'Normalizado para padrão E.164 brasileiro antes de entrar no sistema.'],
                  ['Atividade', 'Ciclo de inatividade (A0 até A17). Variável chave da régua.'],
                  ['Papel', 'Segmentação de fidelidade (Bronze, Prata, Ouro, Platina, Rubi, Diamante). Determina oferta, parcelamento e tom da abordagem.'],
                ].map(([col, papel]) => (
                  <tr key={col} className="hover:bg-slate-50">
                    <td className="p-4 font-mono text-xs text-rose-700">{col}</td>
                    <td className="p-4 text-slate-700">{papel}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8 rounded-2xl border-l-4 border-amber-500 bg-amber-50 p-6">
            <div className="mb-2 flex items-center gap-2 font-semibold text-amber-900">
              <AlertTriangle className="h-5 w-5" />
              <span>Informações que precisamos enriquecer</span>
            </div>
            <p className="mb-3 text-sm text-amber-900">
              A planilha não traz algumas informações importantes para a régua
              funcionar. Vamos resolver cada uma assim:
            </p>
            <ul className="space-y-2 text-sm text-amber-900">
              <li><strong>Central (Cruzeiro, Guará, Caçapava):</strong> de-para cod estrutura → central já recebido em Excel. Importação na Fase 0.</li>
              <li><strong>Marca:</strong> base é multi-marca (Eudora, QDB e Oui). Um único banco - diferenciação de marca vai no criativo e no template por campanha.</li>
              <li><strong>Flag de cor (verde, amarelo, laranja, vermelho):</strong> derivada automaticamente do campo atividade pelo próprio banco.</li>
              <li><strong>Última compra, valor, produtos:</strong> dados ainda presos no CRM da franqueadora. Entram na fase de evolução futura.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* PROJETO 2 */}
      <section className="bg-slate-900 px-6 py-20 text-white">
        <div className="mx-auto max-w-6xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <div className="mb-2 inline-block rounded-full bg-rose-500/20 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-rose-300">
              Projeto 2
            </div>
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">Gestão das 17 Lojas Físicas</h2>
            <p className="mb-12 max-w-3xl text-lg text-slate-300">
              Enquanto o Projeto 1 reativa a base de revendedoras, o Projeto 2 dá
              visibilidade e controle sobre o que acontece nos balcões das lojas.
              Dois projetos independentes que rodam em paralelo.
            </p>
          </motion.div>
          <div className="grid gap-6 md:grid-cols-2">
            {projeto2Componentes.map((comp, i) => (
              <motion.div
                key={comp.titulo}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur"
              >
                <comp.icon className="mb-3 h-8 w-8 text-rose-300" />
                <h3 className="mb-2 text-lg font-semibold">{comp.titulo}</h3>
                <p className="text-sm text-slate-300">{comp.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FASES */}
      <section className="px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">Como vamos executar</h2>
            <p className="mb-12 text-lg text-slate-600">
              Entrega em fases, com MVP rodando em uma central antes de escalar
              para todas. Cada fase tem um entregável validável.
            </p>
          </motion.div>

          <div className="space-y-6">
            {fases.map((fase, i) => (
              <motion.div
                key={fase.numero}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
              >
                <div className={`flex items-center gap-4 bg-gradient-to-r ${fase.cor} p-5 text-white`}>
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20 text-2xl font-bold backdrop-blur">
                    {fase.numero}
                  </div>
                  <div className="flex-1">
                    <div className="text-xs uppercase tracking-wider opacity-80">Fase {fase.numero}</div>
                    <h3 className="text-xl font-bold">{fase.titulo}</h3>
                  </div>
                  <div className="flex items-center gap-2 rounded-full bg-white/20 px-3 py-1 text-sm backdrop-blur">
                    <Clock className="h-4 w-4" />
                    {fase.duracao}
                  </div>
                </div>
                <ul className="space-y-2 p-6">
                  {fase.itens.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-slate-700">
                      <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* RESULTADOS */}
      <section className="bg-gradient-to-br from-rose-50 to-amber-50 px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">Resultados esperados</h2>
            <p className="mb-12 max-w-3xl text-lg text-slate-600">
              Baseado no que você descreveu na reunião. Todos os números serão
              validados por dashboard já no MVP.
            </p>
          </motion.div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {resultadosEsperados.map((r, i) => (
              <motion.div
                key={r.metrica}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm"
              >
                <div className="mb-2 text-4xl font-bold text-rose-600">{r.metrica}</div>
                <p className="text-sm text-slate-600">{r.descricao}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* JA COMECAMOS */}
      <section className="bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900 px-6 py-20 text-white">
        <div className="mx-auto max-w-5xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-2 text-sm backdrop-blur">
              <CheckCircle2 className="h-4 w-4 text-emerald-300" />
              <span>Trabalho ja iniciado</span>
            </div>
            <h2 className="mb-6 text-3xl font-bold md:text-4xl">Nos ja comecamos</h2>
            <p className="mb-8 max-w-3xl text-lg text-emerald-100">
              Antes mesmo de fechar contrato, ja fizemos a auditoria completa da sua operacao.
              20+ conversas lidas, 96 tags mapeadas, 22 automacoes analisadas. Os dados abaixo
              sao reais e extraidos diretamente do seu ManyChat.
            </p>
          </motion.div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { valor: '8.210', label: 'contatos mapeados' },
              { valor: '14', label: 'supervisoras identificadas' },
              { valor: '22', label: 'automacoes analisadas' },
              { valor: '20+', label: 'conversas lidas manualmente' },
            ].map((m, i) => (
              <motion.div key={m.label} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}
                className="rounded-xl border border-white/10 bg-white/5 p-5 backdrop-blur">
                <p className="text-3xl font-bold">{m.valor}</p>
                <p className="mt-1 text-sm text-emerald-200">{m.label}</p>
              </motion.div>
            ))}
          </div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={4} className="mt-8">
            <a href="/boticario/relatorio1" className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 font-semibold text-emerald-900 transition hover:bg-emerald-50">
              Ver relatorio completo <ArrowRight className="h-5 w-5" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* INVESTIMENTO */}
      <section className="px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">Investimento</h2>
            <p className="mb-12 text-lg text-slate-600">
              Com o orçamento de R$8.000/mês confirmado para IA + disparos, o
              sistema se paga retirando 2 funcionários da operação de relacionamento.
              O custo de implantação é único e parcelado ao longo das fases.
            </p>
          </motion.div>

          <div className="space-y-3">
            {investimentoProjeto1.map((inv, i) => (
              <motion.div
                key={inv.item}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="flex items-start gap-4 rounded-xl border border-slate-200 bg-white p-5"
              >
                <CheckCircle2 className="mt-1 h-6 w-6 flex-shrink-0 text-emerald-600" />
                <div>
                  <div className="font-semibold text-slate-900">{inv.item}</div>
                  <div className="text-sm text-slate-600">{inv.detalhe}</div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 rounded-2xl border-l-4 border-rose-500 bg-rose-50 p-6">
            <div className="mb-2 flex items-center gap-2 font-semibold text-rose-900">
              <Layers className="h-5 w-5" />
              <span>Proposta de parceria estratégica</span>
            </div>
            <p className="text-sm text-rose-900">
              Sua operação pode virar um case violento entre os franqueados do
              grupo. Se topar abrir a porta para que outros franqueados conheçam
              o que construímos juntos, o investimento fica mais acessível: o
              projeto vira vitrine e eu trago novos clientes a partir dele.
            </p>
          </div>
        </div>
      </section>

      {/* PONTOS CONFIRMADOS */}
      <section className="bg-slate-50 px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-emerald-700">
              <CheckCircle2 className="h-4 w-4" />
              Alinhamentos confirmados
            </div>
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">Você já fez a parte difícil</h2>
            <p className="mb-8 text-lg text-slate-600">
              As 6 perguntas que emperram projetos como esse — a maioria dos clientes
              demora semanas para responder. Você respondeu em horas. Isso diz tudo
              sobre como você opera. Só falta o go.
            </p>
          </motion.div>
          <ol className="space-y-4">
            {pontosConfirmacao.map((p, i) => (
              <motion.li
                key={p.pergunta}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="flex items-start gap-4 rounded-xl border border-emerald-200 bg-white p-5"
              >
                <CheckCircle2 className="mt-1 h-6 w-6 flex-shrink-0 text-emerald-600" />
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">{p.pergunta}</div>
                  <span className="text-slate-800 font-medium">{p.resposta}</span>
                </div>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      {/* CUSTO DA INACAO */}
      <section className="px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-red-100 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-red-700">
              <TrendingDown className="h-4 w-4" />
              O custo de esperar
            </div>
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">Cada ciclo de 21 dias que passa tem um preço</h2>
            <p className="mb-10 text-lg text-slate-600">
              Não decidir também é uma decisão. E ela tem custo.
            </p>
          </motion.div>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                icon: Users,
                numero: '400+',
                titulo: 'revendedoras sem contato',
                desc: 'A cada ciclo sem régua, sua base inteira fica no silêncio. Nenhuma mensagem, nenhum lembrete, nenhuma oferta. Quem não lembra de você, compra de quem lembrou.',
              },
              {
                icon: TrendingDown,
                numero: '2',
                titulo: 'funcionários pagos para não escalar',
                desc: 'Os R$8.000/mês que vão para 2 pessoas hoje poderiam estar rodando um sistema que não dorme, não falta, não erra o timing e atende 400+ revendedoras ao mesmo tempo.',
              },
              {
                icon: CalendarCheck,
                numero: '21',
                titulo: 'dias é o próximo ciclo',
                desc: 'Se você fechar essa semana, o MVP já pode estar rodando em uma central antes do próximo ciclo completo terminar. Se esperar mais um ciclo, atrasa tudo em 3 semanas.',
              },
            ].map((item, i) => (
              <motion.div
                key={item.titulo}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="rounded-2xl border border-red-100 bg-red-50 p-6"
              >
                <item.icon className="mb-3 h-8 w-8 text-red-500" />
                <div className="mb-1 text-3xl font-bold text-red-600">{item.numero}</div>
                <div className="mb-2 font-semibold text-red-900">{item.titulo}</div>
                <p className="text-sm text-red-800">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROXIMO PASSO */}
      <section className="bg-gradient-to-br from-slate-900 to-rose-950 px-6 py-20 text-white">
        <div className="mx-auto max-w-4xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <div className="mb-8 rounded-2xl border border-emerald-400/30 bg-emerald-500/10 p-5">
              <div className="mb-2 flex items-center gap-2 font-semibold text-emerald-300">
                <Lock className="h-5 w-5" />
                Sem risco na entrada
              </div>
              <p className="text-sm text-slate-300">
                A Fase 0 dura 1 semana e tem entregável concreto: banco modelado,
                de-para validado, BM criada. Você aprova tudo antes de qualquer
                compromisso com as fases seguintes. Se não gostar do que viu,
                para aqui sem custo adicional.
              </p>
            </div>

            <div className="text-center">
              <h2 className="mb-4 text-3xl font-bold md:text-5xl">Só falta o go</h2>
              <p className="mb-3 text-xl text-slate-300">
                Você respondeu as 6 perguntas, mandou o Excel, participou da
                reunião. A parte difícil foi você quem fez.
              </p>
              <p className="mb-10 text-xl font-semibold text-rose-300">
                Clique no botão abaixo e mande a mensagem que já está escrita.
              </p>

              <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
                <a
                  href="https://wa.me/5512997300000?text=Jonathan%2C+pode+come%C3%A7ar.+Quero+ir+para+a+Fase+0."
                  className="inline-flex items-center gap-3 rounded-full bg-emerald-500 px-10 py-5 text-lg font-bold text-white shadow-lg transition hover:bg-emerald-600 hover:shadow-emerald-500/30 hover:shadow-xl"
                >
                  Pode começar — Fase 0
                  <ArrowRight className="h-5 w-5" />
                </a>
                <a
                  href="https://wa.me/5512997300000?text=Jonathan%2C+tenho+uma+d%C3%BAvida+sobre+a+proposta."
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-8 py-5 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/10"
                >
                  Tenho uma dúvida
                </a>
              </div>

              <p className="mt-8 text-xs text-slate-500">
                Proposta válida para início até 18/04/2026 · eBuz Negócios Digitais · Jonathan Machado
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
